import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Printer, Loader2, ArrowLeft } from "lucide-react";
import { buildImageUrl } from "./components/depotvente/DepotVenteListingForm";
import { useSiteMeta } from "./hooks/useSiteMeta";
import { useContactInfo } from "./hooks/useContactInfo";

const API = process.env.REACT_APP_BACKEND_URL;

// Format a French phone number: "+33649070820" -> "06 49 07 08 20"
const formatFrenchPhone = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/[^\d+]/g, "");
  // Convert +33X... to 0X...
  let normalized = digits;
  if (normalized.startsWith("+33")) normalized = "0" + normalized.slice(3);
  else if (normalized.startsWith("0033")) normalized = "0" + normalized.slice(4);
  if (normalized.length === 10) {
    return normalized.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
  }
  return raw;
};

// A4 printable vehicle fiche — opens in a new tab, auto-triggers browser print
// so the technician can hand a professional-looking A4 to on-site buyers.
const PrintableVehiclePage = () => {
  useSiteMeta({ title: "Fiche véhicule — ATR", description: "Fiche véhicule Auvergne Tech Racing", noindex: true });
  const contact = useContactInfo();
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [content, setContent] = useState({});

  useEffect(() => {
    axios
      .get(`${API}/api/content/depot_vente_listings`)
      .then((r) => {
        setContent(r.data || {});
        const found = (r.data?.listings || []).find((l) => l.id === id);
        if (!found) setError("Fiche véhicule introuvable");
        else setListing(found);
      })
      .catch(() => setError("Erreur au chargement"))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="animate-spin text-[#FF3B30]" size={32} />
      </div>
    );
  }
  if (error || !listing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-8 text-center">
        <p className="text-red-600 mb-4">{error || "Fiche introuvable"}</p>
        <a href="/achat-vente-voiture-clermont-ferrand" className="text-[#FF3B30] font-semibold">Retour aux annonces</a>
      </div>
    );
  }

  const allImages = [listing.image_url, ...(Array.isArray(listing.images) ? listing.images : [])].filter(Boolean).slice(0, 6);

  return (
    <div className="printable-root bg-white text-black min-h-screen">
      {/* Print stylesheet */}
      <style>{`
        @page { size: A4; margin: 10mm; }
        @media print {
          body { margin: 0; padding: 0; }
          .no-print { display: none !important; }
          .printable-page { page-break-after: always; }
          .printable-page:last-child { page-break-after: auto; }
        }
        .printable-root { color: #111; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
      `}</style>

      {/* Screen-only toolbar */}
      <div className="no-print sticky top-0 z-50 bg-[#0A0A0A] text-white px-6 py-3 flex items-center justify-between shadow-md">
        <a href="/achat-vente-voiture-clermont-ferrand" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm">
          <ArrowLeft size={14} /> Retour aux annonces
        </a>
        <button
          onClick={handlePrint}
          data-testid="printable-print-btn"
          className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-5 py-2 rounded-md text-sm uppercase tracking-wider"
        >
          <Printer size={14} />
          Imprimer / Enregistrer en PDF
        </button>
      </div>

      {/* A4 page */}
      <div className="printable-page max-w-[210mm] mx-auto p-8 pb-16" data-testid="printable-fiche">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-[#FF3B30] pb-4 mb-6">
          <div>
            <div className="text-[#FF3B30] font-black text-2xl tracking-tight leading-none">AUVERGNE TECH RACING</div>
            <div className="text-gray-500 text-xs mt-1">
              {formatFrenchPhone(content.cta_phone || contact.phone) || contact.phone_display} · {contact.fullAddress}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-gray-400">Fiche véhicule</div>
            <div className="text-[10px] text-gray-400">Édité le {new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</div>
          </div>
        </div>

        {/* Title + status */}
        <div className="mb-4">
          <h1 className="text-3xl font-black leading-tight">{listing.title}</h1>
          <div className="mt-1 flex items-center gap-3 flex-wrap">
            {listing.status === "vendu" && <span className="bg-gray-900 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">Vendu</span>}
            {listing.status === "reserve" && <span className="bg-yellow-400 text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1">Réservé</span>}
            {(!listing.status || listing.status === "disponible") && <span className="bg-green-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">Disponible</span>}
            {listing.price && <span className="text-[#FF3B30] font-black text-2xl">{listing.price}</span>}
          </div>
        </div>

        {/* Cover + gallery */}
        {allImages.length > 0 && (
          <div className="mb-6">
            <div className="w-full aspect-[16/9] bg-gray-100 overflow-hidden rounded">
              <img src={buildImageUrl(allImages[0])} alt={listing.title} className="w-full h-full object-cover" />
            </div>
            {allImages.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-2">
                {allImages.slice(1, 6).map((url, i) => (
                  <div key={i} className="aspect-[4/3] bg-gray-100 overflow-hidden rounded">
                    <img src={buildImageUrl(url)} alt={`Photo ${i + 2}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Key specs */}
        <SpecGrid title="Caractéristiques">
          <PdfSpec label="Marque" value={listing.brand} />
          <PdfSpec label="Modèle" value={listing.model} />
          <PdfSpec label="Finition" value={listing.trim} />
          <PdfSpec label="Année" value={listing.year} />
          <PdfSpec label="1ère immat" value={listing.first_registration} />
          <PdfSpec label="Kilométrage" value={listing.km || listing.exact_km} />
          <PdfSpec label="Carburant" value={listing.fuel} />
          <PdfSpec label="Boîte" value={listing.gearbox} />
          <PdfSpec label="Motorisation" value={listing.engine} />
          <PdfSpec label="Puissance" value={listing.power_din ? `${listing.power_din} ch` : ""} />
          <PdfSpec label="CV fiscaux" value={listing.fiscal_hp} />
          <PdfSpec label="Cylindrée" value={listing.displacement ? `${listing.displacement} cm³` : ""} />
          <PdfSpec label="CO₂" value={listing.co2 ? `${listing.co2} g/km` : ""} />
          <PdfSpec label="Crit'Air" value={listing.crit_air} />
          <PdfSpec label="Portes" value={listing.doors} />
          <PdfSpec label="Places" value={listing.seats} />
          <PdfSpec label="Couleur ext." value={listing.color_exterior} />
          <PdfSpec label="Couleur int." value={listing.color_interior} />
          <PdfSpec label="CT valide" value={listing.ct_valid_until} />
          <PdfSpec label="Propriétaires précédents" value={listing.previous_owners} />
          <PdfSpec label="Nombre de clés" value={listing.keys_count} />
          <PdfSpec label="Carnet d'entretien" value={listing.service_book} />
        </SpecGrid>

        {/* Description */}
        {listing.description && (
          <div className="mb-5">
            <h2 className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2 border-b border-gray-200 pb-1">Description</h2>
            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{listing.description}</p>
          </div>
        )}

        {/* Options */}
        {Array.isArray(listing.options) && listing.options.length > 0 && (
          <div className="mb-5">
            <h2 className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2 border-b border-gray-200 pb-1">Équipements ({listing.options.length})</h2>
            <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-sm">
              {listing.options.map((o, i) => (
                <div key={i} className="text-gray-800">✓ {o}</div>
              ))}
            </div>
          </div>
        )}

        {/* Highlights */}
        {Array.isArray(listing.highlights) && listing.highlights.length > 0 && (
          <div className="mb-5">
            <h2 className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2 border-b border-gray-200 pb-1">Points forts</h2>
            <ul className="text-sm text-gray-800 space-y-1 pl-4 list-disc">
              {listing.highlights.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          </div>
        )}

        {/* Footer contact */}
        <div className="mt-8 pt-4 border-t-2 border-[#FF3B30] text-center">
          <div className="text-[#FF3B30] font-black text-lg">Intéressé ? Contactez-nous</div>
          <div className="text-sm text-gray-700 mt-1">
            📞 <strong>{formatFrenchPhone(content.cta_phone || contact.phone) || contact.phone_display}</strong> · ✉ {contact.email}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {contact.fullAddress} · auvergnetechracing.com
          </div>
        </div>
      </div>
    </div>
  );
};

const SpecGrid = ({ title, children }) => {
  // Filter out null/undefined children so we don't render an empty labelled block
  const visibleChildren = React.Children.toArray(children).filter(Boolean);
  if (visibleChildren.length === 0) return null;
  return (
    <div className="mb-5">
      <h2 className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2 border-b border-gray-200 pb-1">{title}</h2>
      <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-sm">{visibleChildren}</div>
    </div>
  );
};

const PdfSpec = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between border-b border-gray-100 py-0.5">
      <span className="text-gray-500 text-xs">{label}</span>
      <span className="text-gray-900 font-semibold text-xs">{value}</span>
    </div>
  );
};

export default PrintableVehiclePage;
